import type { CSSProperties } from 'react';
import { cn } from '@/utils/cn';
import { LockGlyph } from './LockGlyph';

export type LockBadgeSize = 'xs' | 'sm' | 'md';

interface LockBadgeProps {
  /** Circle diameter: xs = 12px, sm = 14px, md = 16px (default). Glyph scales with it. */
  size?: LockBadgeSize;
  /** Positioning only, e.g. "absolute -right-0.5 -bottom-0.5" or "ml-auto". Never size. */
  className?: string;
}

/** Circle diameter and padlock glyph size in px. Both even, so (circle - glyph) / 2 is an integer offset. */
const SIZE_PX: Record<LockBadgeSize, { circle: number; glyph: number }> = {
  xs: { circle: 12, glyph: 8 },
  sm: { circle: 14, glyph: 10 },
  md: { circle: 16, glyph: 12 },
};

/**
 * Tiny lock marker for entry points that stay visible but are locked by the plan. Decorative: the control that
 * carries it announces "Locked: <feature>" itself (useLockedEntry().ariaLabelFor).
 *
 * Geometry: a perfect circle (explicit equal px width/height/min/max, flex-none so a flex row can never squash it,
 * ring is a box-shadow so it adds no layout size). The padlock is an inline SVG, display:block, in a 16x16 viewBox
 * whose combined shape bounding box (shackle + body) is x 3..13, y 1.5..14.5, i.e. centred exactly on (8, 8).
 */
export const LockBadge = ({ size = 'md', className }: LockBadgeProps) => {
  const { circle, glyph } = SIZE_PX[size];
  const style: CSSProperties = {
    width: circle,
    height: circle,
    minWidth: circle,
    minHeight: circle,
    maxWidth: circle,
    maxHeight: circle,
    padding: 0,
    lineHeight: 0,
    fontSize: 0,
  };
  return (
    <span
      aria-hidden="true"
      data-testid="lock-badge"
      data-size={size}
      style={style}
      className={cn(
        'box-border inline-flex flex-none shrink-0 grow-0 aspect-square items-center justify-center overflow-hidden rounded-full bg-white text-primary-600 ring-1 ring-primary-200',
        className
      )}
    >
      <LockGlyph size={glyph} />
    </span>
  );
};

export default LockBadge;
